import type { ApolloError } from '@apollo/client';
import type { SortOrder } from 'antd/lib/table/interface';
import type { TeamsToDisplay } from '../components/Teams/types';
import {
  GraphQLTypes,
  InputType,
  order_by,
  Selector,
} from '../lib/generated/zeus';
import { useTypedQuery } from '../lib/generated/zeus/apollo';
import { activeRecord, deletedRecord } from '../lib/graphql/common/queries';
import { genericScalars } from '../lib/graphql/helpers';
import { eventSelector } from './selectors/event';

export const allTeamsQueryName = 'queryAllTeams';

const mapToHasuraSortOrder = (order?: SortOrder) =>
  order === 'ascend'
    ? order_by.asc
    : order === 'descend'
      ? order_by.desc
      : undefined;

const teamSelector = Selector('team')({
  id: true,
  name: true,
  team_type: true,
  created_at: true,
  internal_order_number: true,
  record_status: true,
  team_role_assignments: [
    {
      where: activeRecord,
      order_by: [
        {
          team_role: {
            name: order_by.asc,
          },
        },
      ],
    },
    {
      team_role: {
        name: true,
        role_group: true,
      },
      user_role_assignments: [
        {
          where: activeRecord,
        },
        {
          user: {
            id: true,
            first_name: true,
            last_name: true,
            email_address: true,
          },
        },
      ],
    },
  ],
  event_team_deleted: [
    {},
    {
      reason: true,
      event: eventSelector,
    },
  ],
  event_team_reactivated: [
    {},
    {
      reason: true,
      event: eventSelector,
    },
  ],
});

/**
 * Team GraphQL data schema, represents stored data.
 */
export type AllTeamsResult = InputType<
  GraphQLTypes['team'],
  typeof teamSelector,
  typeof genericScalars
>;

type QueryAllTeamsProps = {
  readonly querySoc: boolean;
  readonly teamsToDisplay: TeamsToDisplay;
  readonly search?: string;
  readonly limit?: number;
  readonly offset?: number;
  readonly sortBy?: {
    readonly name?: SortOrder;
    readonly created_at?: SortOrder;
  };
  readonly filteredTeamType?: readonly string[];
} & {
  readonly onError?: (e: ApolloError) => void;
};

/**
 * Provides GraphQL query to fetch all Teams.
 * Note that the query is cached under query name {@link allTeamsQueryName}, so less network round trip unless refetch.
 * @param onError callback to handle errors.
 * @returns custom hook that provides GraphQL query to query all Teams.
 */
const useQueryAllTeams = ({
  querySoc,
  teamsToDisplay,
  search,
  limit,
  offset,
  sortBy,
  filteredTeamType,
  onError,
}: QueryAllTeamsProps) => {
  const teamFilter =
    teamsToDisplay === 'All'
      ? {}
      : teamsToDisplay === 'Archived Only'
        ? deletedRecord
        : activeRecord;

  const result = useTypedQuery(
    {
      team: [
        {
          where: {
            _and: [
              teamFilter,
              {
                _or: [
                  {
                    name: { _ilike: search },
                  },
                  {
                    internal_order_number: { _ilike: search },
                  },
                  {
                    cost_object: { description: { _ilike: search } },
                  },
                ],
              },
            ],
            team_type: querySoc
              ? { _eq: 'SOC' }
              : filteredTeamType !== undefined
                ? { _in: [...filteredTeamType] }
                : { _neq: 'SOC' },
          },
          limit,
          offset,
          order_by: [
            {
              name: mapToHasuraSortOrder(sortBy?.name),
              created_at: mapToHasuraSortOrder(sortBy?.created_at),
            },
          ],
        },
        teamSelector,
      ],
      team_aggregate: [
        {
          where: {
            ...teamFilter,
            name: { _ilike: search },
            team_type: querySoc
              ? { _eq: 'SOC' }
              : filteredTeamType !== undefined
                ? { _in: [...filteredTeamType] }
                : { _neq: 'SOC' },
          },
        },
        { aggregate: { count: [{}, true] } },
      ],
    },
    {
      scalars: genericScalars,
      apolloOptions: {
        onError,
      },
      operationOptions: {
        operationName: allTeamsQueryName,
      },
    }
  );

  return result;
};

export default useQueryAllTeams;
